/**
 * Broadcast title and season helpers.
 *
 * Both exist to stop support typing the same thing three different ways. The
 * title is a DISPLAY LABEL composed from structured fields — teams, date and
 * stage are already columns, so asking a human to retype them into a string
 * just guarantees inconsistency (and broadcaster filenames are worse again:
 * "Round 10 Game 1 - Brisbane @ Sydney ABL 2526").
 */

export interface TitleParts {
  awayTeamName?: string | null
  homeTeamName?: string | null
  stageName?: string | null
  eventDate?: string | null
  fallback?: string | null
}

/**
 * Parse a YYYY-MM-DD as a LOCAL date.
 *
 * new Date('2026-04-02') is UTC midnight, which renders as 1 Apr in any
 * negative-offset timezone — so a game would be titled with the wrong day for
 * every user west of Greenwich.
 */
const localDate = (iso: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

const prettyDate = (iso: string): string => {
  const d = localDate(iso)
  return d ? d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : ''
}

/**
 * `{Away} @ {Home} — {Stage}, {Date}` — the US convention the source filenames
 * already use. Each part is optional; whatever is known gets used.
 */
export const composeTitle = (p: TitleParts): string => {
  const fixture =
    p.awayTeamName && p.homeTeamName
      ? `${p.awayTeamName} @ ${p.homeTeamName}`
      : (p.awayTeamName || p.homeTeamName || '')

  const tail = [p.stageName, p.eventDate ? prettyDate(p.eventDate) : '']
    .filter(Boolean)
    .join(', ')

  if (fixture && tail) return `${fixture} — ${tail}`
  if (fixture) return fixture
  if (tail) return tail
  return (p.fallback || '').replace(/\.[^.]+$/, '')
}

/**
 * Season labels for a league.
 *
 * 'calendar' leagues (MLB, NFL, NWSL) run inside one year  -> "2026"
 * 'split'    leagues (ABL, NBA, NHL, NLL)  cross new year  -> "2025-26"
 *
 * Generated rather than stored in a table: a seasons lookup would need a new
 * row per league per year forever, which is maintenance nobody will do.
 */
export const seasonOptions = (
  format: 'calendar' | 'split' | string | null | undefined,
  around: Date = new Date()
): string[] => {
  const y = around.getFullYear()
  const years = [y + 1, y, y - 1, y - 2, y - 3]
  if (format === 'split') {
    return years.map((start) => `${start}-${String((start + 1) % 100).padStart(2, '0')}`)
  }
  return years.map(String)
}

/**
 * The season a date most likely belongs to, used to preselect. For split
 * leagues a month before July belongs to the season that STARTED last year.
 */
export const likelySeason = (
  format: 'calendar' | 'split' | string | null | undefined,
  eventDate?: string | null
): string => {
  const base = (eventDate ? localDate(eventDate) : null) ?? new Date()
  const y = base.getFullYear()
  if (format === 'split') {
    const start = base.getMonth() < 6 ? y - 1 : y
    return `${start}-${String((start + 1) % 100).padStart(2, '0')}`
  }
  return String(y)
}
